import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { load } from '@cashfreepayments/cashfree-js';
import { CASHFREE_MODE } from '../../config/environment';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Divider,
  Paper,
  Skeleton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Lock, ArrowForward, CalendarToday, Info } from '@mui/icons-material';
import { ColorsConstants, dashboardCardBgColor, grayFontColor } from '../../cssUtils/constants.js';
import {
  POST_CALCULATE_ADDON_CHECKOUT,
  POST_PURCHASE_ADDONS,
} from '../../config/urls.js';
import BackButton from '../../common/back-button.jsx';
import {
  formatPrice as formatPriceUtil,
  formatDate,
  getAddonDisplayName,
} from './utils.js';

const AddonPurchaseCheckout = ({
  selectedAddons = [],
  currentSubscription,
  billingCurrency = 'INR',
  onBack,
}) => {
  // Bind billingCurrency so call sites don't need to pass it each time
  const formatPrice = (price) => formatPriceUtil(price, billingCurrency);

  // ── Classify addons by type ──────────────────────────────────────────────────
  const oneTimeAddons   = selectedAddons.filter((a) => a.addon_type === 'one_time');
  const recurringAddons = selectedAddons.filter((a) => a.addon_type !== 'one_time');
  const isMixed      = oneTimeAddons.length > 0 && recurringAddons.length > 0;
  const isAllOneTime = oneTimeAddons.length > 0 && recurringAddons.length === 0;

  // ── State ───────────────────────────────────────────────────────────────────
  const [breakdown, setBreakdown]               = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [breakdownError, setBreakdownError]     = useState(null);
  const [isProcessing, setIsProcessing]         = useState(false);

  const cashfreeRef = useRef(null);

  // Load Cashfree SDK once
  useEffect(() => {
    const init = async () => {
      if (typeof window === 'undefined') return;
      try {
        const cf = await load({ mode: CASHFREE_MODE });
        if (!cf) { console.error('❌ Cashfree SDK load() returned null'); return; }
        cashfreeRef.current = cf;
      } catch (e) {
        console.error('❌ Failed to load Cashfree SDK:', e);
      }
    };
    init();
  }, []);

  // ── Fetch unified breakdown from backend ────────────────────────────────────
  useEffect(() => {
    const fetchBreakdown = async () => {
      if (!selectedAddons.length) return;

      // Fast client-side guard for recurring addons: check if billing period has ended
      if (recurringAddons.length > 0 && currentSubscription?.end_date) {
        const today   = new Date();
        today.setHours(0, 0, 0, 0);
        const endDate = new Date(currentSubscription.end_date);
        endDate.setHours(0, 0, 0, 0);
        if (endDate <= today) {
          setBreakdownError(
            'Your current billing period has ended or expires today. ' +
            'Please renew your subscription before purchasing add-ons.'
          );
          setBreakdown(null);
          return;
        }
      }

      setLoadingBreakdown(true);
      setBreakdownError(null);

      try {
        const addonIds     = selectedAddons.map((a) => a.id);
        const addonConfigs = selectedAddons
          .filter((a) => a.quantity !== undefined && a.quantity > 0)
          .map((a) => ({ addon_id: a.id, quantity: a.quantity }));

        const response = await axios.post(
          POST_CALCULATE_ADDON_CHECKOUT,
          { addon_ids: addonIds, addon_configs: addonConfigs },
          { headers: { 'X-Tenant-ID': localStorage.getItem('tenant') } },
        );
        setBreakdown(response.data);
      } catch (err) {
        console.error('❌ Failed to fetch addon checkout breakdown:', err);
        setBreakdownError(
          err?.response?.data?.detail || 'Failed to calculate amount. Please try again.'
        );
        setBreakdown(null);
      } finally {
        setLoadingBreakdown(false);
      }
    };

    fetchBreakdown();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedAddons)]);

  // ── Unified payment handler ──────────────────────────────────────────────────
  const handlePayment = async () => {
    if (!cashfreeRef.current) {
      alert('Payment SDK not loaded yet. Please wait a moment.');
      return;
    }
    if (!breakdown) {
      alert('Pricing not loaded yet. Please wait.');
      return;
    }

    setIsProcessing(true);
    try {
      const addonIds     = selectedAddons.map((a) => a.id);
      const addonConfigs = selectedAddons
        .filter((a) => a.quantity !== undefined && a.quantity > 0)
        .map((a) => ({ addon_id: a.id, quantity: a.quantity }));

      const response = await axios.post(
        POST_PURCHASE_ADDONS,
        { addon_ids: addonIds, addon_configs: addonConfigs },
        { headers: { 'X-Tenant-ID': localStorage.getItem('tenant') } },
      );

      const sessionId = response.data.payment_session_id;
      if (sessionId && cashfreeRef.current) {
        cashfreeRef.current.checkout({
          paymentSessionId: sessionId,
          redirectTarget: '_self',
        });
      } else {
        console.error('❌ No payment_session_id in response:', response.data);
        alert('Error: Server did not return a valid session ID.');
      }
    } catch (error) {
      console.error('❌ Addon payment initiation failed:', error);
      alert(
        error?.response?.data?.detail ||
        'Failed to start payment. Please try again.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Derived values ───────────────────────────────────────────────────────────
  const prorationPct = breakdown?.proration_factor != null
    ? Math.round(breakdown.proration_factor * 100)
    : null;

  const currentSubPrice        = parseFloat(currentSubscription?.price || 0);
  const existingAddonsRenewal  = breakdown ? parseFloat(breakdown.existing_addons_renewal_cost || 0) : 0;
  const nextRenewalTotal = (breakdown?.has_recurring && breakdown?.next_renewal_additional != null)
    ? currentSubPrice + existingAddonsRenewal + breakdown.next_renewal_additional
    : null;

  // Accent color based on purchase type
  const accentColor = isAllOneTime ? '#D97706' : isMixed ? '#7C3AED' : ColorsConstants.primaryPurple100;
  const accentHover = isAllOneTime ? '#B45309' : isMixed ? '#6D28D9' : ColorsConstants.primaryBackgroundColor;
  const summaryBg   = isAllOneTime ? '#FFF7ED' : isMixed ? '#F5F3FF' : '#EFF6FF';

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (!selectedAddons.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          No add-ons selected
        </Typography>
        <Button variant="contained" onClick={onBack}
          sx={{ backgroundColor: ColorsConstants.primaryPurple100 }}>
          Go Back to Add-ons
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Back button */}
      <Box sx={{ mb: 2 }}>
        <BackButton onClick={onBack} />
      </Box>

      {/* ── Info banner based on purchase type ── */}
      {isMixed ? (
        <Alert severity="info" icon={<Info />}
          sx={{ mb: 3, borderRadius: 2, backgroundColor: '#F5F3FF', color: '#5B21B6',
                '& .MuiAlert-icon': { color: '#7C3AED' } }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Mixed Add-on Purchase
          </Typography>
          <Typography variant="body2">
            Your selection includes both recurring (prorated) and one-time add-ons.
            They are charged in a single payment. Recurring add-ons auto-renew; one-time add-ons are charged once only.
          </Typography>
        </Alert>
      ) : isAllOneTime ? (
        <Alert severity="info" icon={<Info />}
          sx={{ mb: 3, borderRadius: 2, backgroundColor: '#FFF7ED', color: '#92400E',
                '& .MuiAlert-icon': { color: '#D97706' } }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            One-Time Purchase — Charged Once
          </Typography>
          <Typography variant="body2">
            These add-ons are charged at full price and will not auto-renew.
            Your existing subscription plan and billing cycle remain unchanged.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="info" icon={<Info />}
          sx={{ mb: 3, borderRadius: 2, backgroundColor: '#EFF6FF', color: '#1E40AF',
                '& .MuiAlert-icon': { color: '#1E40AF' } }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Prorated Billing — Mid-Cycle Add-on Purchase
          </Typography>
          <Typography variant="body2">
            You'll only be charged for the remaining days in your current billing period.
            From your next renewal, the full-period rate applies.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ── Left column: add-on cards ── */}
        <Grid item xs={12} md={8}>

          {/* Recurring add-ons section */}
          {recurringAddons.length > 0 && (
            <Card sx={{ mb: 3, border: `1px solid ${ColorsConstants.primaryGray10}` }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3,
                  color: ColorsConstants.primaryBackgroundColor }}>
                  {isMixed ? 'Recurring Add-ons (Prorated)' : 'Add-ons Being Purchased'}
                </Typography>

                {recurringAddons.map((addon, idx) => {
                  const addonDetail = breakdown?.recurring_addon_breakdown?.find(
                    (b) => b.addon_id === addon.id
                  );
                  return (
                    <Box key={addon.id || idx} sx={{ p: 2, mb: 1.5, borderRadius: 1,
                      border: `1px solid ${ColorsConstants.primaryGray10}`,
                      backgroundColor: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle1"
                            sx={{ fontWeight: 600, color: ColorsConstants.primaryBackgroundColor }}>
                            {getAddonDisplayName(addon.name)}
                          </Typography>
                          {addon.quantity > 1 && (
                            <Typography variant="caption" sx={{ color: grayFontColor, display: 'block' }}>
                              {addon.quantity} units
                            </Typography>
                          )}
                          {addonDetail && prorationPct != null && (
                            <Typography variant="caption" sx={{ color: grayFontColor, display: 'block' }}>
                              {addonDetail.additional_quantity} unit(s) × {formatPrice(addonDetail.unit_price)} × {prorationPct}% of period
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          {loadingBreakdown ? (
                            <Skeleton width={80} height={28} />
                          ) : (
                            <>
                              <Typography variant="h6" sx={{ fontWeight: 'bold',
                                color: ColorsConstants.primaryBackgroundColor }}>
                                {addonDetail
                                  ? formatPrice(addonDetail.prorated_cost)
                                  : formatPrice(addon.price)}
                              </Typography>
                              <Chip label="Prorated" size="small" color="info"
                                sx={{ fontSize: '0.65rem', height: 18 }} />
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* One-time add-ons section */}
          {oneTimeAddons.length > 0 && (
            <Card sx={{ mb: 3, border: `1px solid ${ColorsConstants.primaryGray10}` }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3,
                  color: ColorsConstants.primaryBackgroundColor }}>
                  One-Time Add-ons
                </Typography>

                {oneTimeAddons.map((addon, idx) => (
                  <Box key={addon.id || idx} sx={{ p: 2, mb: 1.5, borderRadius: 1,
                    border: `1px solid ${ColorsConstants.primaryGray10}`,
                    backgroundColor: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle1"
                          sx={{ fontWeight: 600, color: ColorsConstants.primaryBackgroundColor }}>
                          {addon.displayName || getAddonDisplayName(addon.name)}
                        </Typography>
                        {addon.description && (
                          <Typography variant="caption" sx={{ color: grayFontColor, display: 'block' }}>
                            {addon.description}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold',
                          color: ColorsConstants.primaryBackgroundColor }}>
                          {formatPrice(addon.price)}
                        </Typography>
                        <Chip label="One-Time" size="small"
                          sx={{ fontSize: '0.65rem', height: 18,
                            backgroundColor: '#FEF3C7', color: '#92400E' }} />
                      </Box>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Proration detail card (recurring addons only) */}
          {breakdown && !loadingBreakdown && breakdown.has_recurring && (
            <Card sx={{ border: `1px solid ${ColorsConstants.primaryGray10}` }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2,
                  color: ColorsConstants.primaryBackgroundColor }}>
                  Proration Calculation
                </Typography>
                <Box sx={{ p: 2, borderRadius: 2, backgroundColor: dashboardCardBgColor }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>Billing Period</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatDate(breakdown.period_start_date)} → {formatDate(breakdown.period_end_date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>Total Days in Period</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{breakdown.total_days} days</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>Remaining Days</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                      {breakdown.remaining_days} days
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>Proration Factor</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: ColorsConstants.primaryPurple100 }}>
                      {breakdown.remaining_days}/{breakdown.total_days} = {prorationPct}% of period
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* ── Right column: order summary ── */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 100,
            border: `1px solid ${ColorsConstants.primaryGray10}` }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3,
                color: ColorsConstants.primaryBackgroundColor }}>
                Order Summary
              </Typography>

              {/* Billing context (shown for recurring / mixed) */}
              {!isAllOneTime && (
                <Paper sx={{ p: 2, mb: 2, backgroundColor: dashboardCardBgColor }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: ColorsConstants.primaryGray70 }}>
                    <CalendarToday sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
                    Billing Details
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>Billing Cycle</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                      {currentSubscription?.billing_cycle || '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>Current Period Ends</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatDate(currentSubscription?.end_date)}
                    </Typography>
                  </Box>
                </Paper>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5,
                color: ColorsConstants.primaryBackgroundColor }}>
                {!isAllOneTime && !isMixed ? 'Immediate Charge (Prorated)' : 'Charge Breakdown'}
              </Typography>

              {breakdownError && (
                <Alert severity="error" sx={{ mb: 2 }}>{breakdownError}</Alert>
              )}

              <Box sx={{ mb: 2 }}>
                {/* Mixed: show the two subtotals separately before the combined line */}
                {isMixed && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: grayFontColor }}>
                        Recurring Add-ons (Prorated)
                      </Typography>
                      {loadingBreakdown
                        ? <Skeleton width={60} />
                        : <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatPrice(breakdown?.prorated_subtotal ?? 0)}
                          </Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: grayFontColor }}>
                        One-Time Add-ons
                      </Typography>
                      {loadingBreakdown
                        ? <Skeleton width={60} />
                        : <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatPrice(breakdown?.one_time_subtotal ?? 0)}
                          </Typography>}
                    </Box>
                    <Divider sx={{ my: 1 }} />
                  </>
                )}

                {/* Combined subtotal (always shown) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: grayFontColor }}>Subtotal</Typography>
                  {loadingBreakdown
                    ? <Skeleton width={60} />
                    : <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatPrice(breakdown?.combined_subtotal ?? 0)}
                      </Typography>}
                </Box>

                {/* Tax breakdown (server-computed, shown for all types) */}
                {!loadingBreakdown && breakdown?.tax_breakdown?.map((tax, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>
                      {tax.name} ({tax.rate_display}%)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatPrice(tax.amount)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Pay Now total */}
              <Paper sx={{ p: 1.5, mb: 3, backgroundColor: summaryBg, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold',
                    color: ColorsConstants.primaryBackgroundColor }}>
                    Pay Now
                  </Typography>
                  {loadingBreakdown
                    ? <Skeleton width={80} height={40} />
                    : <Typography variant="h4" sx={{ fontWeight: 'bold', color: accentColor }}>
                        {formatPrice(breakdown?.immediate_total)}
                      </Typography>}
                </Box>
              </Paper>

              <Divider sx={{ my: 2 }} />

              {/* Next renewal context (recurring or mixed) */}
              {!isAllOneTime && !loadingBreakdown && breakdown?.has_recurring && nextRenewalTotal != null && (
                <Paper sx={{ p: 2, mb: 3, backgroundColor: '#F0FDF4', borderRadius: 2,
                  border: '1px solid #BBF7D0' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#166534' }}>
                    At Next Renewal ({formatDate(currentSubscription?.end_date)})
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#15803D' }}>Existing Plan</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534' }}>
                      {formatPrice(currentSubPrice)}
                    </Typography>
                  </Box>
                  {existingAddonsRenewal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#15803D' }}>Existing Add-ons</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534' }}>
                        {formatPrice(existingAddonsRenewal)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#15803D' }}>New Add-ons</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534' }}>
                      {formatPrice(breakdown.next_renewal_additional)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5, borderColor: '#BBF7D0' }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#166534' }}>
                      New Total (excl. GST)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#166534' }}>
                      {formatPrice(nextRenewalTotal)}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* Payment button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handlePayment}
                disabled={isProcessing || loadingBreakdown || !!breakdownError || !breakdown}
                startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <Lock />}
                endIcon={!isProcessing && <ArrowForward />}
                sx={{
                  py: 1.5,
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  backgroundColor: accentColor,
                  '&:hover': { backgroundColor: accentHover },
                  '&.Mui-disabled': { backgroundColor: ColorsConstants.primaryGray20 },
                }}
              >
                {isProcessing ? 'Processing...' : 'Proceed to Payment'}
              </Button>

              {/* Security badge */}
              <Paper sx={{ mt: 2, p: 2, backgroundColor: dashboardCardBgColor, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: grayFontColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                  <Lock fontSize="small" />
                  Secure payment powered by Cashfree
                </Typography>
                <Typography variant="caption" sx={{ color: grayFontColor }}>
                  Add-ons activate immediately after payment
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddonPurchaseCheckout;
